import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { track } from '@vercel/analytics'
import { Button } from './ui/button'
import { motion, AnimatePresence } from 'framer-motion'
import StarBorder from './ui/StarBorder'
import { useSupabaseAuth } from '../lib/useSupabaseAuth'

const PublicHeader = ({ onOpenAuth, onStartTrial }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useSupabaseAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Disable body scroll when menu is open (iOS-compatible)
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Save current scroll position
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
      document.body.style.overflow = 'hidden'
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.overflow = ''
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
      }
    }
    // Cleanup on unmount
    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])
  
  const isActive = (path) => {
    // Don't mark hash-only links as active (like /#features)
    if (path.includes('#')) {
      return false
    }
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  const navItems = [
    { name: 'Home', href: '/', showOnLanding: false },
    { name: 'Features', href: '/features' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Blog', href: '/blog' },
    { name: "FAQ's", href: '/faqs' },
    { name: 'About', href: '/about' }
  ]

  // Filter out Home link if we're on landing page, and hide About and Blog for all public pages
  const displayItems = location.pathname === '/'
    ? navItems.filter(item => item.showOnLanding !== false && item.name !== 'About' && item.name !== 'Blog')
    : navItems.filter(item => item.name !== 'About' && item.name !== 'Blog')

  // Mobile menu items
  const mobileMenuItems = [
    { name: 'Pricing', href: '/pricing' },
    { name: 'Features', href: '/features' },
    { name: 'AI', href: '/prosperia-ai' },
    { name: 'About Us', href: '/about' },
    ...(location.pathname !== '/' ? [{ name: 'Home', href: '/' }] : [])
  ]

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <>
    <header className="bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <motion.div 
            className="flex items-center group cursor-pointer"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" onClick={() => track('Header - Logo')} className="flex items-center gap-2">
              <picture>
                <source srcSet="/icons/logo_1024x1024-noBGm.webp" type="image/webp" />
                <img
                  src="/icons/logo_1024x1024-noBGm.png"
                  alt="Prosperia Logo"
                  className="h-11 w-11 object-contain -ml-1"
                  width="44"
                  height="44"
                  loading="eager"
                  fetchPriority="high"
                />
              </picture>
              <span className="text-2xl font-bold text-white group-hover:text-mint-400 transition-colors duration-300">
                Prosperia CRM
              </span>
            </Link>
          </motion.div>
          
          {/* Pill Navigation */}
          <motion.nav 
            className="hidden md:flex"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="flex items-center gap-1 p-1.5 bg-slate-800/80 rounded-full backdrop-blur-sm border border-slate-700/50 shadow-sm">
              {displayItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => track(`Header Nav - ${item.name}`)}
                  className={`relative px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 group ${
                    isActive(item.href)
                      ? 'text-mint-400'
                      : 'text-slate-300 hover:text-mint-400'
                  }`}
                >
                  <span className="relative z-10">{item.name}</span>
                  <span className={`absolute inset-0 bg-slate-700 rounded-full transition-all duration-300 ${
                    isActive(item.href)
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  } shadow-md`}></span>
                </Link>
              ))}
            </div>
          </motion.nav>
          
          {/* Auth Buttons */}
          <motion.div 
            className="flex items-center gap-2 sm:gap-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {isAuthenticated ? (
              <Button
                variant="ghost"
                onClick={() => {
                  track('Header - Dashboard')
                  navigate('/dashboard')
                }}
                className="text-slate-200 hover:text-mint-400 hover:bg-slate-800 rounded-full px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm transition-all duration-300"
                aria-label="Go to dashboard"
              >
                <i className="fas fa-tachometer-alt mr-1 sm:mr-2"></i>
                <span className="hidden sm:inline">Dashboard</span>
                <span className="sm:hidden">Dash</span>
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    track('Header - Sign In')
                    onOpenAuth()
                  }}
                  className="text-slate-200 hover:text-mint-400 hover:bg-slate-800 rounded-full px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm transition-all duration-300"
                  aria-label="Sign in"
                >
                  Sign In
                </Button>
                <StarBorder
                  as="div"
                  color="teal"
                  speed="6s"
                  thickness={2}
                  className="rounded-full"
                >
                  <Button
                    onClick={() => {
                      track('Header - Start Free Trial')
                      onStartTrial()
                    }}
                    className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-3 sm:px-6 py-1.5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    aria-label="Start free"
                  >
                    <i className="fas fa-rocket mr-1 sm:mr-2"></i>
                    <span className="hidden sm:inline">Start Free</span>
                    <span className="sm:hidden">Free</span>
                  </Button>
                </StarBorder>
              </>
            )}
          </motion.div>

          {/* Mobile Menu Button */}
          <motion.button
            className={`md:hidden p-2 rounded-full hover:bg-slate-800 transition-colors duration-300 ${
              isMobileMenuOpen ? 'fixed top-4 right-4 z-[120]' : 'relative z-50'
            }`}
            onClick={handleMobileMenuToggle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            <div className="relative w-6 h-6">
              <span
                className={`absolute top-0 left-0 w-full h-0.5 bg-slate-200 transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-2.5' : ''
                }`}
              />
              <span
                className={`absolute top-2.5 left-0 w-full h-0.5 bg-slate-200 transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <span
                className={`absolute top-5 left-0 w-full h-0.5 bg-slate-200 transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-2.5' : ''
                }`}
              />
            </div>
          </motion.button>
        </div>
      </div>
    </header>

    {/* Mobile Menu Drawer - Outside header to avoid stacking context issues */}
    <AnimatePresence>
      {isMobileMenuOpen && (
        <>
          {/* Backdrop - semi-transparent overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] md:hidden touch-action-none"
            onClick={handleMobileMenuClose}
          />

          {/* Drawer - slides in from right */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-slate-900/98 backdrop-blur-xl z-[110] md:hidden flex flex-col shadow-2xl border-l border-slate-700/50"
          >
            {/* Header with Logo and Close Button */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-700/50">
              <Link 
                to="/"
                onClick={() => {
                  track('Mobile Menu - Logo')
                  handleMobileMenuClose()
                }}
                className="flex items-center gap-2 group"
              >
                <picture>
                  <source srcSet="/icons/logo_1024x1024-noBGm.webp" type="image/webp" />
                  <img
                    src="/icons/logo_1024x1024-noBGm.png"
                    alt="Prosperia Logo"
                    className="h-10 w-10 object-contain"
                    width="40"
                    height="40"
                    loading="eager"
                  />
                </picture>
                <span className="text-xl font-bold text-white group-hover:text-teal-400 transition-colors duration-300">
                  Prosperia CRM
                </span>
              </Link>
              
              {/* Close Button */}
              <button
                onClick={handleMobileMenuClose}
                className="p-2 rounded-full hover:bg-slate-800 transition-colors duration-300"
                aria-label="Close menu"
              >
                <div className="relative w-6 h-6">
                  <span className="absolute top-0 left-0 w-full h-0.5 bg-slate-200 rotate-45 translate-y-2.5 transition-all duration-300" />
                  <span className="absolute top-2.5 left-0 w-full h-0.5 bg-slate-200 opacity-0" />
                  <span className="absolute top-5 left-0 w-full h-0.5 bg-slate-200 -rotate-45 -translate-y-2.5 transition-all duration-300" />
                </div>
              </button>
            </div>

            {/* Navigation Links - Large Touch-Friendly Buttons */}
            <nav className="flex-1 overflow-y-auto px-4 py-6">
              <div className="space-y-3">
                {mobileMenuItems.map((item, index) => {
                  const isActive = location.pathname === item.href || 
                    (item.href !== '/' && location.pathname.startsWith(item.href))
                  
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={handleMobileMenuClose}
                      className={`block w-full px-6 py-4 rounded-xl transition-all duration-300 group ${
                        isActive 
                          ? 'bg-teal-500/20 border-2 border-teal-400/50 shadow-lg shadow-teal-500/10' 
                          : 'bg-slate-800/50 border-2 border-slate-700/50 hover:bg-slate-800 hover:border-teal-400/30'
                      }`}
                    >
                      <span 
                        className={`block text-lg font-semibold transition-colors duration-300 ${
                          isActive 
                            ? 'text-teal-400 font-bold' 
                            : 'text-white group-hover:text-teal-400'
                        }`}
                      >
                        {item.name}
                      </span>
                      {isActive && (
                        <span className="block text-xs text-teal-400/70 mt-1 font-medium">
                          Current page
                        </span>
                      )}
                    </Link>
                  )
                })}
              </div>
            </nav>

            {/* Footer with CTA */}
            <div className="px-4 py-6 border-t border-slate-700/50 space-y-3">
              {!isAuthenticated && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      track('Mobile Menu - Sign In')
                      handleMobileMenuClose()
                      onOpenAuth()
                    }}
                    className="w-full text-slate-200 hover:text-teal-400 hover:bg-slate-800 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-300"
                    aria-label="Sign in"
                  >
                    Sign In
                  </Button>
                  <StarBorder
                    as="div"
                    color="teal"
                    speed="6s"
                    thickness={2}
                    className="rounded-xl"
                  >
                    <Button
                      onClick={() => {
                        track('Mobile Menu - Start Free Trial')
                        handleMobileMenuClose()
                        onStartTrial()
                      }}
                      className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-4 py-3 rounded-xl text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                      aria-label="Start free"
                    >
                      <i className="fas fa-rocket mr-2"></i>
                      Start Free
                    </Button>
                  </StarBorder>
                </>
              )}
              {isAuthenticated && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    track('Mobile Menu - Dashboard')
                    handleMobileMenuClose()
                    navigate('/dashboard')
                  }}
                  className="w-full text-slate-200 hover:text-teal-400 hover:bg-slate-800 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-300"
                  aria-label="Go to dashboard"
                >
                  <i className="fas fa-tachometer-alt mr-2"></i>
                  Go to Dashboard
                </Button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </>
  )
}

export default PublicHeader

